// Installer calls carry a header token; staff calls use the existing JWT bridge.
import { bindInstaller, currentInstaller, isNetworkError } from '../installer/pwa/offlineQueue';
import { reportClientEvent } from '../lib/clientLog';
async function send(url, opts) {
  try { return await fetch(url, opts); }
  catch (error) {
    if (isNetworkError(error)) throw new TypeError('اتصال برقرار نشد. مدارک روی گوشی مانده است؛ بعد از اتصال دوباره تلاش کنید.');
    throw error;
  }
}
async function jsonFetch(url, opts = {}) {
  const res = await send(url, opts);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const error = new Error(body.error || 'درخواست انجام نشد. دوباره تلاش کنید.');
    error.status = res.status; error.code = body.code; error.ticket_ids = body.ticket_ids;
    // The tester-visible tracking code; it ties the error to the server's log line.
    error.requestId = res.headers?.get?.('x-request-id') || undefined;
    throw error;
  }
  return body;
}

// Stage evidence bytes are sealed by their SHA-256 at commit, so the stored blob
// goes up unchanged (never through uploadViaSignedUrl, whose HEIC conversion
// re-encodes). Storage answering Duplicate means a lost-response PUT already landed.
export async function putSigned(url, blob) {
  let res;
  try { res = await send(url, { method: 'PUT', headers: { 'Content-Type': 'image/jpeg' }, body: blob }); }
  // Storage is another origin, so no X-Request-Id comes back: the report's own rid is
  // the tracking code that finds the client.stage_upload_failed server line.
  catch (error) { throw Object.assign(error, { requestId: reportClientEvent('stage_upload_failed', { status: 0, host: hostOf(url) }) }); }
  if (res.ok || res.status === 409) return;
  if (res.status === 400 && /Duplicate/.test(await res.text().catch(() => ''))) return;
  const error = new Error('بارگذاری عکس انجام نشد. دوباره تلاش کنید.');
  error.status = res.status; error.code = 'upload_failed';
  error.requestId = reportClientEvent('stage_upload_failed', { status: res.status, host: hostOf(url) });
  throw error;
}

// Storage is another origin: report its host only, never the signed URL.
function hostOf(url) {
  try { return new URL(url, window.location.origin).host; } catch { return undefined; }
}

const sessionHeaders = (token) => ({ 'X-Session-Token': token });
const sessionJson = (token) => ({ ...sessionHeaders(token), 'Content-Type': 'application/json' });
const assertSession = (token) => {
  if (!token || localStorage.getItem('installer_session_token') !== token) throw new Error('نشست نیروی اجرایی تغییر کرد. دوباره وارد شوید.');
};

async function measureClockOffset(token, url) {
  if (!navigator.onLine) return null;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 3000);
  try {
    // Express serves HEAD through its GET handler. The installer SW caches GET only,
    // so this Date header is a fresh clock sample, independent of a cached job body.
    const res = await fetch(url, { method: 'HEAD', cache: 'no-store', headers: sessionHeaders(token), signal: controller.signal });
    const date = res.headers.get('Date');
    const serverTime = date ? Date.parse(date) : NaN;
    return res.ok && Number.isFinite(serverTime) ? serverTime - Date.now() : null;
  } catch { return null; } // Offline or slow links keep the draft's last calibration.
  finally { clearTimeout(timer); }
}

// Jobs progress uses StageView vocabulary: locked/open/in_review/failed/passed.
// T41b may supply review_phase (ai/human), effective review_mode and waiting_since.
// Accept raw submitted/ai_reviewing/awaiting_supervisor/skipped during rollout.
// Missing mode/time must never invent a supervisor or a zero-minute wait.
export function installerProgressStage(progress) {
  const raw = progress.current_state;
  const state = ['submitted', 'ai_reviewing', 'awaiting_supervisor'].includes(raw) ? 'in_review' : raw === 'skipped' ? 'passed' : raw;
  let review_phase = progress.review_phase || (raw === 'awaiting_supervisor' ? 'human' : null);
  if (review_phase === 'human' && progress.review_mode !== 'ai_only'
    && (progress.review_mode !== 'ai_then_supervisor' || !Number.isFinite(Date.parse(progress.waiting_since)))) review_phase = null;
  return { ...progress, state, review_phase };
}

export const installerApi = {
  operations: (token,id) => jsonFetch(`/api/installer/jobs/${id}/operations`,{headers:sessionHeaders(token)}),
  appointment: (token,id,tehran_at) => jsonFetch(`/api/installer/jobs/${id}/appointment`,{method:'POST',headers:sessionJson(token),body:JSON.stringify({tehran_at})}),
  holdTicket: (token,id,body) => jsonFetch(`/api/installer/jobs/${id}/hold/capture-tickets`,{method:'POST',headers:sessionJson(token),body:JSON.stringify(body)}),
  reportHold: (token,id,body) => jsonFetch(`/api/installer/jobs/${id}/hold`,{method:'POST',headers:sessionJson(token),body:JSON.stringify(body)}),
  sendCustomerProof: (token,id,channel) => jsonFetch(`/api/installer/jobs/${id}/customer/send`,{method:'POST',headers:sessionJson(token),body:JSON.stringify({channel})}),
  verifyCustomerProof: (token,id,code) => jsonFetch(`/api/installer/jobs/${id}/customer/verify`,{method:'POST',headers:sessionJson(token),body:JSON.stringify({code})}),
  warnings: (token, id) => jsonFetch(`/api/installer/jobs/${id}/warnings`, { headers: sessionHeaders(token) }),
  ackWarnings: (token, id, warning_ids) => jsonFetch(`/api/installer/jobs/${id}/warnings/acknowledge`, { method: 'POST', headers: sessionJson(token), body: JSON.stringify({ warning_ids }) }),
  followups: (token) => jsonFetch('/api/installer/followups', { headers: sessionHeaders(token) }),
  followup: (token, id) => jsonFetch(`/api/installer/followups/${id}`, { headers: sessionHeaders(token) }),
  followupTicket: (token, id, body) => jsonFetch(`/api/installer/followups/${id}/capture-tickets`, { method: 'POST', headers: sessionJson(token), body: JSON.stringify(body) }),
  submitFollowup: (token, id, body) => jsonFetch(`/api/installer/followups/${id}/attempts`, { method: 'POST', headers: sessionJson(token), body: JSON.stringify(body) }),
  requestOtp: (phone) => jsonFetch('/api/installer/auth/request-otp', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone }),
  }),
  verifyOtp: async (phone, otp) => {
    const result = await jsonFetch('/api/installer/auth/verify-otp', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone, otp }),
    });
    if (result.exists) {
      localStorage.setItem('installer_session_token', result.session_token);
      await installerApi.me(result.session_token);
    }
    return result;
  },
  logout: (token) => jsonFetch('/api/installer/auth/logout', { method: 'POST', headers: sessionJson(token), body: '{}' }),
  profile: async (token) => {
    assertSession(token);
    const result = await jsonFetch('/api/installer/me/profile', { headers: sessionHeaders(token) });
    assertSession(token);
    await bindInstaller(result.id, token);
    return result;
  },
  updateProfile: (token, patch) => jsonFetch('/api/installer/me/profile', {
    method: 'POST', headers: sessionJson(token), body: JSON.stringify(patch),
  }),
  deleteAccount: (token) => jsonFetch('/api/installer/me', { method: 'DELETE', headers: sessionJson(token), body: '{}' }),
  signedUrl: (kind, format, credential = {}) => {
    const { session_token, ...rest } = credential;
    return jsonFetch('/api/installer/uploads/signed-url', {
      method: 'POST', headers: session_token ? sessionJson(session_token) : { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, format, ...rest }),
    });
  },
  signup: (payload) => jsonFetch('/api/installer/signup', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  me: async (token) => {
    assertSession(token);
    const result = await jsonFetch('/api/installer/me', { headers: sessionHeaders(token) });
    assertSession(token);
    await bindInstaller(result.id, token);
    return result;
  },
  jobs: async (token) => {
    assertSession(token);
    if (!currentInstaller(token)) await installerApi.me(token);
    const result = await jsonFetch('/api/installer/jobs', { headers: sessionHeaders(token) });
    assertSession(token);
    return result;
  },
  job: async (token, id, { syncClock = false } = {}) => {
    assertSession(token);
    if (!currentInstaller(token)) await installerApi.me(token);
    const url = `/api/installer/jobs/${id}`;
    const [result, clockOffset] = await Promise.all([
      jsonFetch(url, { headers: sessionHeaders(token) }),
      syncClock ? measureClockOffset(token, url) : null,
    ]);
    assertSession(token);
    return { ...result, clock_offset_ms: clockOffset };
  },
  startJob: (token, id) => jsonFetch(`/api/installer/jobs/${id}/start`, {
    method: 'POST', headers: sessionJson(token), body: '{}',
  }),
  completeJob: (token, id, photos, note, bundleItemResults) => jsonFetch(`/api/installer/jobs/${id}/complete`, {
    method: 'POST', headers: sessionJson(token), body: JSON.stringify({ photos, note, ...(bundleItemResults ? { bundleItemResults } : {}), capture: 'camera-v1' }),
  }),
  appealStage: (token, id, stageId, body) => jsonFetch(`/api/installer/jobs/${id}/stages/${stageId}/appeal`, {
    method: 'POST', headers: sessionJson(token), body: JSON.stringify(body),
  }),
  submitStageAttempt: (token, id, jobStageId, body) => jsonFetch(`/api/installer/jobs/${id}/stages/${jobStageId}/attempts`, {
    method: 'POST', headers: sessionJson(token), body: JSON.stringify(body),
  }),
  captureTicket: (token, id, jobStageId, body) => jsonFetch(`/api/installer/jobs/${id}/stages/${jobStageId}/capture-tickets`, {
    method: 'POST', headers: sessionJson(token), body: JSON.stringify(body),
  }),
  captureUploadUrl: (token, ticketId) => jsonFetch(`/api/installer/capture-tickets/${ticketId}/upload-url`, {
    method: 'POST', headers: sessionJson(token), body: '{}',
  }),
  presenceOnline: (token, lat, lng) => jsonFetch('/api/installer/presence/online', {
    method: 'POST', headers: sessionJson(token), body: JSON.stringify({ lat, lng }),
  }),
  presenceOffline: (token) => jsonFetch('/api/installer/presence/offline', { method: 'POST', headers: sessionJson(token), body: '{}' }),
  offers: (token) => jsonFetch('/api/installer/offers', { headers: sessionHeaders(token) }),
  acceptOffer: async (token, id) => {
    const res = await fetch(`/api/installer/offers/${id}/accept`, { method: 'POST', headers: sessionJson(token), body: '{}' });
    const body = await res.json().catch(() => ({}));
    return { status: res.status, ok: res.ok, ...body };
  },
  declineOffer: (token, id) => jsonFetch(`/api/installer/offers/${id}/decline`, { method: 'POST', headers: sessionJson(token), body: '{}' }),
  pushPublicKey: (token) => jsonFetch('/api/installer/push/public-key', { headers: sessionHeaders(token) }),
  pushSubscribe: (token, subscription) => jsonFetch('/api/installer/push/subscribe', {
    method: 'POST', headers: sessionJson(token), body: JSON.stringify({ subscription }),
  }),
  pushUnsubscribe: (token, endpoint) => jsonFetch('/api/installer/push/subscribe', {
    method: 'DELETE', headers: sessionJson(token), body: JSON.stringify({ endpoint }),
  }),
};

// The signed-URL PUT goes straight to Supabase Storage, whose buckets enforce
// allowed_mime_types - so an empty or unrecognized file.type (common for iPhone
// HEIC photos and some camera apps) gets the upload REJECTED outright. Resolve a
// storage-acceptable Content-Type: file.type, else the file extension, else the
// caller's format hint, else a photo default.
const EXT_MIME = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp',
  heic: 'image/heic', heif: 'image/heic', pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};
export function resolveUploadContentType(file, format) {
  if (file && typeof file.type === 'string' && file.type) return file.type;
  const name = file && typeof file.name === 'string' ? file.name : '';
  const ext = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
  if (EXT_MIME[ext]) return EXT_MIME[ext];
  if (format === 'pdf') return 'application/pdf';
  if (format === 'docx') return EXT_MIME.docx;
  if (format === 'png') return 'image/png';
  return 'image/jpeg';
}

// iPhones shoot HEIC by default; the storage upload now accepts it, but the
// vision LLMs cannot READ HEIC bytes - so convert HEIC/HEIF to JPEG in the
// browser first. heic2any (libheif/wasm, ~1.4MB) is lazy-imported only when a
// HEIC file is actually picked, so it never bloats the main bundle.
export async function maybeConvertHeic(file) {
  const isHeic = !!file && (
    (typeof file.type === 'string' && /image\/(heic|heif)/i.test(file.type))
    || (typeof file.name === 'string' && /\.(heic|heif)$/i.test(file.name))
  );
  if (!isHeic) return file;
  const heic2any = (await import('heic2any')).default;
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
  const blob = Array.isArray(out) ? out[0] : out;
  const base = (typeof file.name === 'string' ? file.name : 'photo').replace(/\.(heic|heif)$/i, '');
  return new File([blob], `${base}.jpg`, { type: 'image/jpeg' });
}

export async function uploadViaSignedUrl(kind, file, credential, format) {
  const finalFile = await maybeConvertHeic(file);
  const { signedUrl, path } = await installerApi.signedUrl(kind, format, credential);
  const contentType = resolveUploadContentType(finalFile, format);
  const host = hostOf(signedUrl);
  let put;
  try { put = await fetch(signedUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: finalFile }); }
  catch (error) { throw Object.assign(error, { requestId: reportClientEvent('upload_failed', { status: 0, host }) }); }
  if (!put.ok) {
    // A status lets the outbox treat a 4xx as a refusal instead of retrying it forever.
    const error = new Error('آپلود فایل ناموفق بود.');
    error.status = put.status; error.code = 'upload_failed';
    error.requestId = reportClientEvent('upload_failed', { status: put.status, host });
    throw error;
  }
  return path;
}

// Contractor (پیمانکار) field-role API. The long-lived session_token is sent in the
// X-Session-Token HEADER (never the URL/body) so it stays out of logs, browser
// history, and the Referer header - including for state-changing DELETEs.
const ctHeaders = (token, extra = {}) => ({ 'X-Session-Token': token, ...extra });
const ctJson = (token) => ctHeaders(token, { 'Content-Type': 'application/json' });
export const contractorApi = {
  followups: (token, id) => jsonFetch(`/api/contractor/jobs/${id}/followups`, { headers: sessionHeaders(token) }),
  requestOtp: (phone) => jsonFetch('/api/contractor/auth/request-otp', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone }),
  }),
  verifyOtp: (phone, otp) => jsonFetch('/api/contractor/auth/verify-otp', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone, otp }),
  }),
  logout: (token) => jsonFetch('/api/contractor/auth/logout', { method: 'POST', headers: ctHeaders(token) }),
  signup: (payload) => jsonFetch('/api/contractor/signup', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  me: (token) => jsonFetch('/api/contractor/me', { headers: ctHeaders(token) }),
  profile: (token) => jsonFetch('/api/contractor/me/profile', { headers: ctHeaders(token) }),
  updateProfile: (token, patch) => jsonFetch('/api/contractor/me/profile', {
    method: 'POST', headers: ctJson(token), body: JSON.stringify(patch),
  }),
  installers: (token) => jsonFetch('/api/contractor/installers', { headers: ctHeaders(token) }),
  addInstaller: (token, phone) => jsonFetch('/api/contractor/installers', {
    method: 'POST', headers: ctJson(token), body: JSON.stringify({ phone }),
  }),
  removeInstaller: (token, id) => jsonFetch(`/api/contractor/installers/${id}`, { method: 'DELETE', headers: ctHeaders(token) }),
  jobs: (token) => jsonFetch('/api/contractor/jobs', { headers: ctHeaders(token) }),
  candidates: (token, id) => jsonFetch(`/api/contractor/jobs/${id}/candidates`, { headers: ctHeaders(token) }),
  distribute: (token, id, installer_id) => jsonFetch(`/api/contractor/jobs/${id}/distribute`, {
    method: 'POST', headers: ctJson(token), body: JSON.stringify({ installer_id }),
  }),
  takeBack: (token, id) => jsonFetch(`/api/contractor/jobs/${id}/take-back`, { method: 'POST', headers: ctHeaders(token) }),
  documents: (token, id) => jsonFetch(`/api/contractor/jobs/${id}/documents`, { headers: ctHeaders(token) }),
  uploadDocument: (token, id, payload) => jsonFetch(`/api/contractor/jobs/${id}/documents`, {
    method: 'POST', headers: ctJson(token), body: JSON.stringify(payload),
  }),
  score: (token) => jsonFetch('/api/contractor/score', { headers: ctHeaders(token) }),
  jobDefects: (token, jobId) => jsonFetch(`/api/contractor/jobs/${jobId}/defects`, { headers: ctHeaders(token) }),
};

export const customerAdminApi = {
  list:    (token, status) => jsonFetch(`/api/admin/customer-registrations${status ? `?status=${status}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
  detail:  (token, id) => jsonFetch(`/api/admin/customer-registrations/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  approve: (token, id, autoDispatch) => jsonFetch(`/api/admin/customer-registrations/${id}/approve`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ auto_dispatch: !!autoDispatch }) }),
  reject:  (token, id, reason) => jsonFetch(`/api/admin/customer-registrations/${id}/reject`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }) }),
  reopen:  (token, id) => jsonFetch(`/api/admin/customer-registrations/${id}/reopen`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
};

export const installerAdminApi = {
  list: (token, status) => jsonFetch(`/api/admin/installers?status=${status}`, { headers: { Authorization: `Bearer ${token}` } }),
  detail: (token, id) => jsonFetch(`/api/admin/installers/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  approve: (token, id) => jsonFetch(`/api/admin/installers/${id}/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  reject: (token, id, reason) => jsonFetch(`/api/admin/installers/${id}/reject`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }),
  jobs: (token, status) => jsonFetch(`/api/admin/jobs${status ? `?status=${status}` : ''}`, { headers: { Authorization: `Bearer ${token}` } }),
  createJob: (token, payload) => jsonFetch('/api/admin/jobs', {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  }),
  assign: (token, id, installer_id) => jsonFetch(`/api/admin/jobs/${id}/assign`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ installer_id }),
  }),
  assignCompany: (token, id, contractor_id) => jsonFetch(`/api/admin/jobs/${id}/assign`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ contractor_id }),
  }),
  contractorJobs: (token, id) => jsonFetch(`/api/admin/contractors/${id}/jobs`, { headers: { Authorization: `Bearer ${token}` } }),
  dispatch: (token, id) => jsonFetch(`/api/admin/jobs/${id}/dispatch`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  prepareInspection: (token, id) => jsonFetch(`/api/admin/jobs/${id}/prepare-inspection`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  linkInspection: (token, id, inspection_id) => jsonFetch(`/api/admin/jobs/${id}/link-inspection`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ inspection_id }) }),
  jobOffers: (token, id) => jsonFetch(`/api/admin/jobs/${id}/offers`, { headers: { Authorization: `Bearer ${token}` } }),
  onlineInstallers: (token) => jsonFetch('/api/admin/installers/online', { headers: { Authorization: `Bearer ${token}` } }),
  // Nassabeh: read/write which inspection engine the installer app uses.
  inspectionConfig: (token) => jsonFetch('/api/admin/inspection-config', { headers: { Authorization: `Bearer ${token}` } }),
  setInspectionConfig: (token, variant) => jsonFetch('/api/admin/inspection-config', {
    method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ variant }),
  }),
  // Nassabeh: installer quality score (100 - penalties) + the penalty list.
  installerScore: (token, id) => jsonFetch(`/api/admin/installers/${id}/score`, { headers: { Authorization: `Bearer ${token}` } }),
  // Nassabeh: admin defect review.
  jobDefects: (token, id) => jsonFetch(`/api/admin/jobs/${id}/defects`, { headers: { Authorization: `Bearer ${token}` } }),
  setDefectStatus: (token, defectId, status) => jsonFetch(`/api/admin/defects/${defectId}`, {
    method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
  }),
  // Nassabeh final increment: admin management of contractors (پیمانکار).
  contractors: (token, status) => jsonFetch(`/api/admin/contractors?status=${status}`, { headers: { Authorization: `Bearer ${token}` } }),
  contractorDetail: (token, id) => jsonFetch(`/api/admin/contractors/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
  approveContractor: (token, id) => jsonFetch(`/api/admin/contractors/${id}/approve`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }),
  rejectContractor: (token, id, reason) => jsonFetch(`/api/admin/contractors/${id}/reject`, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ reason }),
  }),
  contractorScore: (token, id) => jsonFetch(`/api/admin/contractors/${id}/score`, { headers: { Authorization: `Bearer ${token}` } }),
  assignInstallerContractor: (token, installerId, contractor_id) => jsonFetch(`/api/admin/installers/${installerId}/contractor`, {
    method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ contractor_id }),
  }),
  // Which work families (لست‌مایل / عمرانی) an installer may be offered or assigned.
  setWorkFamilies: (token, installerId, work_families, reason) => jsonFetch(`/api/admin/installers/${encodeURIComponent(installerId)}/work-families`, {
    method: 'PUT', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ work_families, reason }),
  }),
};
